import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { GitCompare, Loader2, CheckCircle2, ArrowRight, ArrowLeft, BarChart3, Database, SlidersHorizontal, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { DiffSection, AttrChangeCard } from '@/components/CompareDiff';
import { PromoteDiffButton } from '@/components/PromoteDiffButton';
import { useTerms, getTerm } from '@/lib/branding';

export default function ReportComparePage() {
  useTerms();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [leftId, setLeftId] = useState('');
  const [rightId, setRightId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.getReports().then(r => {
      const named = (r || []).filter(x => x.name);
      setReports(named);
      if (named.length >= 2) { setLeftId(named[0].report_id); setRightId(named[1].report_id); }
      else if (named.length === 1) setLeftId(named[0].report_id);
    }).catch(e => toast.error(e.message));
  }, []);

  const runCompare = async () => {
    if (!leftId || !rightId) return toast.error(`Select two ${getTerm('insights', 'plural', 'lower')}`);
    if (leftId === rightId) return toast.error(`Select two different ${getTerm('insights', 'plural', 'lower')}`);
    setLoading(true); setResult(null);
    try { setResult(await api.compareReports(leftId, rightId)); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const nameOf = (id) => reports.find(r => r.report_id === id)?.name || id;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="report-compare-page">
      <button onClick={() => navigate('/dashboards')} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-insights">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to {getTerm('insights')}
      </button>
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-primary/10 border border-primary/20"><GitCompare className="w-6 h-6 text-primary" /></div>
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">Compare {getTerm('insights')}</h1>
          <p className="text-sm text-muted-foreground mt-1">See exactly what differs between two {getTerm('insights', 'plural', 'lower')} — query, visuals and display settings.</p>
        </div>
      </div>

      <Card className="glass-card rounded-2xl border-border/50 p-5">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
          <div className="flex-1 space-y-1.5">
            <label className="text-[11px] text-muted-foreground font-medium">Left {getTerm('insights', 'singular', 'lower')} (baseline)</label>
            <Select value={leftId} onValueChange={setLeftId}>
              <SelectTrigger data-testid="compare-left-select"><SelectValue placeholder={`Select ${getTerm('insights', 'singular', 'lower')}`} /></SelectTrigger>
              <SelectContent>{reports.map(r => <SelectItem key={r.report_id} value={r.report_id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <GitCompare className="w-5 h-5 text-muted-foreground hidden sm:block mb-2.5" />
          <div className="flex-1 space-y-1.5">
            <label className="text-[11px] text-muted-foreground font-medium">Right {getTerm('insights', 'singular', 'lower')} (compare to)</label>
            <Select value={rightId} onValueChange={setRightId}>
              <SelectTrigger data-testid="compare-right-select"><SelectValue placeholder={`Select ${getTerm('insights', 'singular', 'lower')}`} /></SelectTrigger>
              <SelectContent>{reports.map(r => <SelectItem key={r.report_id} value={r.report_id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={runCompare} disabled={loading} className="rounded-xl" data-testid="run-compare-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GitCompare className="w-4 h-4 mr-2" />} Compare
          </Button>
        </div>
      </Card>

      {result && (
        <div className="space-y-4" data-testid="compare-result">
          <div className="flex items-center justify-between flex-wrap gap-2 px-1">
            <div className="text-sm text-muted-foreground">
              <span className="text-rose-400 font-medium">{nameOf(result.left.report_id)}</span>
              <ArrowRight className="w-3.5 h-3.5 inline mx-2" />
              <span className="text-emerald-400 font-medium">{nameOf(result.right.report_id)}</span>
            </div>
            {result.identical
              ? <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20" data-testid="identical-badge"><CheckCircle2 className="w-3 h-3 mr-1" /> {getTerm('insights')} are identical</Badge>
              : <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-amber-400 border-amber-500/30">Differences found</Badge>
                  <PromoteDiffButton resourceType="insight" diff={result} disabled={result.identical} />
                </div>}
          </div>

          <AttrChangeCard title={`${getTerm('insights', 'singular')} Metadata`} icon={FileText} changes={result.report_attributes} testid="diff-section-metadata" />
          <AttrChangeCard title="Query (model, dimensions, measures, filters)" icon={Database} changes={result.query} testid="diff-section-query" />
          <DiffSection title="Visuals" icon={BarChart3} diff={result.visuals} />
          <AttrChangeCard title="Display Settings" icon={SlidersHorizontal} changes={result.settings} testid="diff-section-settings" />

          {result.identical && (
            <Card className="glass-card rounded-2xl border-border/50 px-5 py-4 text-xs text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> These two {getTerm('insights', 'plural', 'lower')} are identical across query, visuals and settings.
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
